import { ReactNode, useEffect, useState } from "react";

import capitalize from "lodash/capitalize";
import {
  ArrowLeftRight,
  Check,
  Circle,
  CircleDot,
  Loader2,
} from "lucide-react";
import { Link } from "react-router";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Segmented } from "@/components/pieces/form/Segmented";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  LedgerAccountType,
  TransferCreditsDialogDocument,
  TransferCreditsMutation,
  useTransferCreditsDialogQuery,
  useTransferCreditsMutation,
} from "@/graphql";
import { formatCredits, parseDecimal } from "@/utils/string";

type TransferOptionTypes = "to_target" | "to_source";

interface ResultsProps {
  result: TransferCreditsMutation["transferCredits"];
  onClose: () => void;
}

function Results({ result, onClose }: ResultsProps) {
  const resultView =
    result && result.ok ? (
      <Alert className="border-green-200 bg-green-50">
        <Check className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          Credits successfully transfered
        </AlertDescription>
      </Alert>
    ) : (
      <Alert variant="destructive">
        <AlertDescription>
          {result?.error?.message ?? "Something went wrong"}
        </AlertDescription>
      </Alert>
    );

  return (
    <div className="space-y-4">
      {resultView}
      <Button className="w-full" size="lg" onClick={() => onClose()}>
        Close
      </Button>
    </div>
  );
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  workspace: "Workspace",
  user: "User",
  workspace_group: "Group",
  workspace_member: "User",
  workspace_group_member: "Member",
  program: "Program",
};

interface AccountOptionsProps {
  isMulti: boolean;
  account?: LedgerAccountType;
  accounts: LedgerAccountType[];
  selectedAccount?: LedgerAccountType;
  onSelect: (account: LedgerAccountType) => void;
}

function AccountOptions({
  isMulti,
  account,
  accounts,
  selectedAccount,
  onSelect,
}: AccountOptionsProps) {
  return (
    <div className="p-3 border-border border rounded-md text-sm">
      {isMulti ? (
        <div className="flex flex-col space-y-2">
          {accounts.map((item) => {
            const isSelected = item?.id === selectedAccount?.id;
            return (
              <div
                key={item?.id}
                className="flex items-center cursor-pointer py-2"
                onClick={() => {
                  onSelect(item);
                }}
              >
                {isSelected ? (
                  <CircleDot className="h-4 w-4 text-blue-600" />
                ) : (
                  <Circle className="h-4 w-4 text-gray-400" />
                )}
                <div className="flex-1 ml-2">{item.label}</div>
                <div className="flex items-center justify-end min-w-[120px]">
                  <EmbedSvg src={CreditSVG} height={14} width={14} />
                  <span className="ml-1">{formatCredits(item.balance)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center">
          <div className="flex-1">{account?.label}</div>
          <div className="flex items-center justify-end min-w-[120px]">
            <EmbedSvg src={CreditSVG} height={14} width={14} />
            <span className="ml-1">{formatCredits(account?.balance)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface TransferCreditsDialogProps {
  source: string | null;
  target: string | null;
  open: boolean;
  onComplete: (success: boolean) => void;
  onClose: () => void;
  visibleBuyCredits?: boolean;
  subInfo?: ReactNode;
  sourceAccounts?: LedgerAccountType[];
  targetAccounts?: LedgerAccountType[];
}

export function TransferCreditsDialog({
  source,
  target,
  open,
  onComplete,
  onClose,
  visibleBuyCredits = false,
  subInfo = null,
  sourceAccounts = [],
  targetAccounts = [],
}: TransferCreditsDialogProps) {
  const [amount, setAmount] = useState<string>("0");
  const [transferType, setTransferType] = useState("to_target");
  const isMultiAccount = sourceAccounts.length > 0;

  const [defaultSourceAccount, setDefaultSourceAccount] = useState<
    LedgerAccountType | undefined
  >(sourceAccounts.length > 0 ? sourceAccounts[0] : undefined);

  const [result, setResult] =
    useState<TransferCreditsMutation["transferCredits"]>();

  const defaultTargetAccount =
    targetAccounts.length > 0 ? targetAccounts[0] : undefined;

  const [targetType] = target ? target.split(":") : [];
  const targetLabel = isMultiAccount
    ? capitalize(defaultTargetAccount?.ownerType ?? "")
    : ACCOUNT_TYPE_LABELS[targetType];

  const { data, refetch } = useTransferCreditsDialogQuery({
    variables: {
      keys: [source, target],
    },
    skip: !source,
  });

  const ledgerAccounts = data?.ledgerAccounts || [];

  // Not yet support multi target accounts
  const [sourceAccount, targetAccount] = isMultiAccount
    ? [defaultSourceAccount, defaultTargetAccount]
    : ledgerAccounts || [
        {
          id: null,
          label: "",
          balance: "0.00",
          name: "",
        },
        {
          id: null,
          label: "",
          balance: "0.00",
          name: "",
        },
      ];

  const [fromAccount, toAccount] =
    transferType === "to_target"
      ? [sourceAccount, targetAccount]
      : [targetAccount, sourceAccount];

  const [transfer, { loading: transfering }] = useTransferCreditsMutation({
    onCompleted: (data) => {
      const { transferCredits } = data;
      if (transferCredits?.ok) {
        setAmount("0.00");
      }
      setResult(transferCredits);
      onComplete(transferCredits?.ok ?? false);
      refetch({
        keys: [source, target],
      });
    },
    onError: () => {
      setResult({ ok: false });
    },
    refetchQueries: isMultiAccount
      ? [
          {
            query: TransferCreditsDialogDocument,
            variables: {
              keys: [source, target],
            },
          },
        ]
      : [],
  });

  const handleTransfer = () => {
    if (!fromAccount?.id || !toAccount?.id) return;
    transfer({
      variables: {
        fromId: fromAccount.id,
        toId: toAccount.id,
        amount,
      },
    });
  };

  const amountAsNumber = parseDecimal(amount);
  const fromAccountBalance = parseDecimal(fromAccount?.balance);

  useEffect(() => {
    if (amountAsNumber > fromAccountBalance) {
      setAmount(fromAccountBalance.toString());
    }
  }, [transferType]);

  const TRANSFER_TYPES: { label: string; value: TransferOptionTypes }[] = [
    {
      label: `Transfer to ${targetLabel}`,
      value: "to_target",
    },
    {
      label: `Transfer from ${targetLabel}`,
      value: "to_source",
    },
  ];

  const canTransfer =
    transfering ||
    !sourceAccount?.id ||
    !targetAccount?.id ||
    !amount ||
    amountAsNumber <= 0 ||
    amountAsNumber > fromAccountBalance;

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        } else {
          setResult(undefined);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Transfer Credits
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <DialogInner className="space-y-4">
            <Results result={result} onClose={onClose} />
          </DialogInner>
        ) : (
          <>
            <DialogInner className="space-y-4">
              {subInfo}

              <div className="space-y-1">
                <Segmented
                  value={transferType}
                  options={TRANSFER_TYPES}
                  onChange={setTransferType}
                />
              </div>

              <div className="space-y-5">
                <div>
                  <div className="font-semibold mb-2">From</div>
                  <AccountOptions
                    isMulti={
                      isMultiAccount &&
                      transferType === "to_target" &&
                      sourceAccounts?.length > 1
                    }
                    account={fromAccount}
                    accounts={sourceAccounts}
                    selectedAccount={defaultSourceAccount}
                    onSelect={setDefaultSourceAccount}
                  />
                </div>

                {visibleBuyCredits && (
                  <div className="text-sm text-gray-600">
                    Need more credits?{" "}
                    <Link to="/buy" className="text-blue-600 hover:underline">
                      Buy credits
                    </Link>{" "}
                    or talk to your Exec rep
                  </div>
                )}

                <div>
                  <div className="font-semibold mb-2">To</div>
                  <AccountOptions
                    isMulti={
                      isMultiAccount &&
                      transferType === "to_source" &&
                      sourceAccounts?.length > 1
                    }
                    account={toAccount}
                    accounts={sourceAccounts}
                    selectedAccount={defaultSourceAccount}
                    onSelect={setDefaultSourceAccount}
                  />
                </div>

                <div>
                  <div className="font-semibold mb-2">Transfer Amount</div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      className="w-24 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={amount}
                      onChange={(e) => {
                        const valueAsNumber = parseFloat(e.target.value) || 0;
                        if (valueAsNumber < 0) {
                          setAmount("0");
                        } else if (valueAsNumber > fromAccountBalance) {
                          setAmount(fromAccountBalance.toString());
                        } else if (isNaN(valueAsNumber)) {
                          setAmount("0");
                        } else {
                          setAmount(e.target.value);
                        }
                      }}
                    />
                    <span className="text-sm text-gray-600">Credits</span>
                  </div>

                  {fromAccountBalance < amountAsNumber && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertDescription>
                        Insufficient credits to make transfer
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </DialogInner>
            <DialogFooter>
              <Button
                disabled={canTransfer || transfering}
                onClick={handleTransfer}
              >
                {transfering ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Confirm Transfer"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
